import React, { useState } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { useCookies } from 'react-cookie'
import { Amplify } from "aws-amplify";

import LogIn from './components/authentication/Login'
import Spinner from './components/loader';
import NavBar from './components/navigation/NavigationBar';
import FileUpload from './components/fileupload/FileUpload';
import TrackFile from './components/filetracking/FileTracking';
import Analytics from './components/analytics/Analytics';
import ClimateToolbox from './components/climate_toolbox/ClimateToolbox';
import Monitoring from './components/monitoring/Monitoring';
import LogOut from './components/authentication/Logout';
import { AuthProvider } from './components/authentication/Authprovider';
import { check_analytics_access, check_ecs_access, check_admin_access, check_abs_access, check_monitoring_access} from './helpers/AccessHelper';
import cognito_config from './cognito-config.json'
import env from './env.json'
import "./main.css";
import "./exaloan-primereact-theme.css"
import "./exaloan-primereact-theme-dark.css"
import "./design-system.css"
import "./ui-kit.css"
import { ThemeContext } from "./context/ThemeContext";
import { LayoutContext } from "./context/LayoutContext";
import { ProtectedRoute } from "./components/authentication/ProtectedRoute";
import { useLocalStorage } from "./helpers/useLocalStorage";
import { Platforms } from "./components/platforms/Platforms";
import { PlatformConfiguration } from "./components/platforms/PlatformConfiguration";
import { PlatformUsers } from "./components/platforms/Users/PlatformUsers";
import { PlatformApiAccess } from "./components/platforms/ApiAccess/PlatformApiAccess";
import  ABSCalculator from "./components/abs_calculator/ABSCalculator"
import PlatformOverviewDetails from "./components/platforms/Overview/PlatformOverviewDetails";
import CashBalance from "./components/cash_balance/cash_balance";
import LoanCheck from "./components/investment_reconciliation/investment_reconciliation"
import OriginatorsData from "./components/data/data"
import PlatformReport from "./components/reporting/PlatformReport"
import CompartmentReport from "./components/compartment_report/compartment_report"
import FeatureGate from "./components/common/FeatureGate"
import Activity from "./components/activity/Activity"
import PlatformActivity from "./components/activity/PlatformActivity"

var DEBUG = env.DEBUG;
if (!DEBUG) {
  if (!window.console) window.console = {};
  var methods = ["log", "debug", "warn", "info", "error", "trace", "table", "dir"];
  for (var i = 0; i < methods.length; i++) {
    console[methods[i]] = function () { };
  }
}

Amplify.configure({
  aws_cognito_region: cognito_config.region,
  aws_user_pools_id: cognito_config.pool_id,
  aws_user_pools_web_client_id: cognito_config.web_client_id,
});


export const App = () => {
  const [cookies, setCookie, removeCookie] = useCookies(['token']);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useLocalStorage("user", null);
  const [groups, setGroups] = useLocalStorage("groups", null);
  const [platform, setPlatform] = useLocalStorage("platform", null);
  const [platform_config, setPlatformConfig] = useLocalStorage("platform_config", null);
  // App sits *above* AuthProvider in the tree (it renders <AuthProvider>
  // itself), so it can't call useAuth() for these - they're plain state kept
  // in sync by NavBar (which is inside AuthProvider and does call useAuth())
  // via the setGroups/setPlatform/setIsViewingAs props below. Before this,
  // setPlatform/setIsViewingAs weren't wired at all: View As updated
  // AuthContext's groups/platform (correctly re-scoping Sidebar.js's nav,
  // which reads useAuth() directly) but never touched App's own copies here,
  // so route gates below that check `platform` (this one) stayed frozen at
  // the real admin's identity the whole time - functionally harmless for
  // gates that only check `groups` (View As's groups prop WAS kept in sync),
  // but wrong for anything that also special-cases `platform` itself.
  const [is_viewing_as, setIsViewingAs] = useState(false);
  // Same rule as the backend's isExaloanAccount: no platform, or "Exaloan".
  const is_exaloan_account = !platform || platform.toLowerCase() === "exaloan";
  // Persisted across sessions (same convention as user/groups/platform
  // above) - both exaloan-primereact-theme.css and its dark counterpart
  // (exaloan-primereact-theme-dark.css, every selector scoped under
  // [data-theme="dark"]) are always loaded; only this attribute on <html>
  // decides which one's rules actually apply, so switching is instant with
  // no stylesheet swap/FOUC. Chart colors (chart_theme.js) read this same
  // ThemeContext directly rather than the DOM attribute, since Chart.js
  // options are plain JS, not CSS.
  const [theme, setTheme] = useLocalStorage("theme", "light");

  // Sidebar rail (collapsed) is a per-browser preference like theme above;
  // the mobile drawer (nav_open) is transient and always starts closed.
  const [collapsed, setCollapsed] = useLocalStorage("sidebar_collapsed", false);
  const [nav_open, setNavOpen] = useState(false);

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  return (

    <div className={`main app-root${collapsed ? " is-collapsed" : ""}${nav_open ? " is-nav-open" : ""}`}>
      <ThemeContext.Provider value={{ theme, setTheme }}>
      <LayoutContext.Provider value={{ collapsed, setCollapsed, nav_open, setNavOpen }}>
      <BrowserRouter>
        <AuthProvider>
          <NavBar setGroups={setGroups} setPlatform={setPlatform} setIsViewingAs={setIsViewingAs} />
          {loading === true ? <div className="loading"><Spinner /> </div> : ""}
          <Routes>
            <Route path="/login" element={<LogIn setLoading={setLoading} setGroups={setGroups} />} />

            <Route element={<ProtectedRoute />}>
              {/* Dashboard (data.js) is the landing page now - open to every
                  authenticated user, not just Monitoring/Admin, same as File
                  Upload always was: GET /overview already scopes an external
                  account's own token down to just its own platform row
                  server-side, so there's nothing left for a group check here
                  to protect that the data itself doesn't already restrict. */}
              <Route path="/" exact={true} element={<OriginatorsData setLoading={setLoading} />} />
              <Route path="/file-upload" element={<FileUpload setLoading={setLoading} />} />
              <Route path="/filetracking" element={<TrackFile setLoading={setLoading} />} />
              <Route path="/logout" element={<LogOut setLoading={setLoading} />} />
              {check_analytics_access(groups) && <Route path="/analytics" element={<Analytics />} />}
              {check_ecs_access(groups) && <Route path="/climate-toolbox" element={<ClimateToolbox setLoading={setLoading} />} />}
              {check_admin_access(groups) && <Route path="/admin" element={<Monitoring setLoading={setLoading} />} />}
              {check_abs_access(groups) && <Route path="/abs" element={<ABSCalculator />} />}
              {check_monitoring_access(groups) && <Route path="cash-balance" element={<CashBalance setLoading={setLoading}/>} />}
              {check_monitoring_access(groups) && <Route path="loan_check" element={<LoanCheck setLoading={setLoading}/>} />}
              {/* Minimal local-only test harness for compartment_report_calc_service/
                  compartment_report_pdf_service - see backend/api/compartment_report.js
                  and CompartmentReport.js's own header comment. Same Monitoring
                  gate as its cash-balance/loan_check sidebar siblings. */}
              {check_monitoring_access(groups) && <Route path="compartment-report" element={<FeatureGate feature="compartment_report"><CompartmentReport setLoading={setLoading}/></FeatureGate>} />}
              {check_admin_access(groups) && <Route path="platforms" element={<Platforms setLoading={setLoading} />} />}
              {/* Same reasoning as "/" above - the merged Dashboard's rows only
                  ever link here for Exaloan staff (external accounts get their
                  own detail rendered inline instead, see data.js), and
                  GET /overview/:platformName already 403s a non-Exaloan token
                  requesting any platform that isn't its own. */}
              <Route path="dashboard/:platformName" element={<PlatformOverviewDetails setLoading={setLoading}/>} />
              {check_admin_access(groups) && <Route path="platforms/:platformName"  element={<PlatformConfiguration  setLoading={setLoading}/> } />}
              {check_admin_access(groups) && <Route path="platforms/:platformName/users"  element={<PlatformUsers  setLoading={setLoading}/> } />}
              {check_admin_access(groups) && <Route path="platforms/:platformName/api-access" element={<PlatformApiAccess />} />}
              {/* Exaloan staff and onboarded platforms' own accounts alike -
                  the backend scopes every Platform Report request to the
                  account's own platform (generate_platform_report.js), and
                  the page auto-selects that platform for a non-Exaloan
                  account. The sidebar link (navConfig.js) only appears for a
                  platform account once its platform is onboarded; this route
                  doesn't wait on that check, since a non-onboarded platform
                  reaching it by URL just gets an empty platform list. The
                  backend's PLATFORM_REPORT_ENABLED is still the kill switch. */}
              {check_analytics_access(groups) &&
                <Route path="platform-report" element={<FeatureGate feature="platform_report"><PlatformReport setLoading={setLoading} /></FeatureGate>} />}
              {/* Pipeline runs, failure reasons and integration logs per
                  platform (backend/api/activity.js, Monitoring-gated). */}
              {/* Exaloan staff only - the backend also 403s platform accounts. */}
              {check_monitoring_access(groups) && is_exaloan_account && <Route path="activity" element={<Activity />} />}
              {check_monitoring_access(groups) && is_exaloan_account && <Route path="activity/:platformKey" element={<PlatformActivity />} />}
              {/* {check_admin_access(groups) && <Route path="overview" element={<Overview setLoading={setLoading} />} />} */}
              
            </Route>
            <Route path="*" render={() => <Navigate to="/" />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </LayoutContext.Provider>
      </ThemeContext.Provider>
    </div>

  );
};

export default App;
